import { z } from 'zod'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { isPlainObject } from '@omnea-app/packages/universal/type-guards/isPlainObject'
import { ReferenceMeta, referenceRegistry } from './refKinds'

/**
 * Reading a zod schema's structure, so the walk can descend it alongside a value.
 *
 * Deliberately no `instanceof`: a second copy of zod in the tree makes every check silently false,
 * and a walk that quietly finds nothing is the one failure this must not have.
 */

export type Schema = z.core.$ZodType

/** Zod's own union of every definition it can produce, so a `switch` on `type` narrows to the real
 * shape and a field renamed upstream stops the build rather than reading `undefined` at runtime. */
export type SchemaNode = z.core.$ZodTypes['_zod']['def']

/** One member of that union, selected by its `type`, so callers can name a definition shape without
 * reaching into zod themselves. */
export type SchemaNodeOf<T extends SchemaNode['type']> = Extract<SchemaNode, { type: T }>

/**
 * A schema's definition, which carries everything below it.
 *
 * `.def` is public, but only on zod's classic `ZodType` — descending a schema hands back core types,
 * which carry the definition under `_zod`. This is the one and only place that reaches for it.
 */
export const definitionOf = (schema: Schema): SchemaNode => (schema as z.core.$ZodTypes)._zod.def

/** A schema stripped of its wrappers, with every annotation and every schema found on the way down. */
export type Unwrapped = { schema: Schema; meta: ReferenceMeta; layers: Schema[] }

// A `lazy` that resolves to itself would spin forever here. No real schema nests anywhere near this
// deep, so reaching the cap means a cycle rather than an unusually deep schema.
const MAX_UNWRAP_DEPTH = 20

const metaOn = (schema: Schema): ReferenceMeta => referenceRegistry.get(schema) ?? {}

// A transform has no input shape of its own, so a pipe above one describes the stored value on its
// output side. `z.preprocess` builds exactly that, and reading `in` would hand back the transform.
const pipeInputIsATransform = (def: z.core.$ZodPipeDef): boolean => definitionOf(def.in).type === 'transform'

/**
 * The schema underneath any wrappers, and every annotation declared along the way.
 *
 * Metadata is collected down the whole chain rather than read at the leaf, because an annotation is
 * as likely to be written above a wrapper as below one — `refOn(z.uuid().optional(), …)` and
 * `ref(…).optional()` describe the same field. Outermost wins, so the nearest declaration to the
 * reader is the one that counts.
 *
 * A wrapper is recognised by carrying an `innerType`, which is how zod itself models one, rather
 * than by being named in a list. A list is an allowlist, and anything missing from it hides whatever
 * it wraps.
 *
 * Resolving `lazy` is what lets the walk descend the recursive condition tree.
 */
export const unwrapSchema = (schema: Schema): Unwrapped => {
  let current = schema
  let meta: ReferenceMeta = {}
  const layers: Schema[] = []

  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
    meta = { ...metaOn(current), ...meta }
    layers.push(current)
    const def = definitionOf(current)

    if (def.type === 'lazy') current = def.getter()
    else if (def.type === 'pipe') current = pipeInputIsATransform(def) ? def.out : def.in
    else if ('innerType' in def) current = def.innerType
    else return { schema: current, meta, layers }
  }

  // Returning the wrapper would send its subtree to a construct the walk cannot descend, and lose
  // every annotation under it without a word.
  throw new Error(`reference walk: schema nests deeper than ${MAX_UNWRAP_DEPTH} wrappers`)
}

/** The schema underneath the wrappers, for callers that do not need the annotations. */
export const innerSchema = (schema: Schema): Schema => unwrapSchema(schema).schema

/** Every annotation declared on a schema or on any wrapper above it. */
export const referenceMetaOf = (schema: Schema): ReferenceMeta => unwrapSchema(schema).meta

/**
 * The values a schema pins down, which only a literal does.
 *
 * An enum is deliberately excluded. A discriminated union declares which field identifies a branch
 * and that field is read separately; an ordinary enum field is just a field, and its values are
 * retired over time — treating one as identifying would rule out a branch holding a value the
 * schema no longer lists, which is precisely the stored config this walk exists to read.
 */
const fixedValues = (schema: Schema): readonly unknown[] | undefined => {
  const def = definitionOf(innerSchema(schema))
  return def.type === 'literal' ? def.values : undefined
}

// Absence contradicts nothing, and `null` counts as absence: a nullable tag genuinely holds it, and
// ruling a branch out on one would lose everything the branch declares.
const disagreesWith = (schema: Schema, value: unknown): boolean => {
  const fixed = fixedValues(schema)
  return fixed !== undefined && value !== undefined && value !== null && !fixed.includes(value)
}

// The fields that identify a branch: the declared discriminator where a union names one, and any
// literal-valued field otherwise.
const tagFieldsOf = (schema: Schema, discriminator: string | undefined): [string, Schema][] => {
  const def = definitionOf(innerSchema(schema))
  if (def.type !== 'object') return []

  const candidates = Object.entries(def.shape).filter(([key]) => !discriminator || key === discriminator)
  return candidates.filter(([, field]) => fixedValues(field) !== undefined)
}

export const OPAQUE_TYPES = ['any', 'unknown', 'custom', 'map', 'set', 'function'] as const

/**
 * A schema nothing can be read beneath: one that admits any document describes none in particular,
 * and JSON holds no Map, Set or function for the rest to match.
 */
export const isOpaque = (def: SchemaNode): def is SchemaNodeOf<(typeof OPAQUE_TYPES)[number]> =>
  OPAQUE_TYPES.some((type) => type === def.type)

export const isUuidString = (schema: Schema): boolean => {
  const def = definitionOf(innerSchema(schema))
  return def.type === 'string' && 'format' in def && def.format === 'uuid'
}

/** The field a union names as identifying its branches, where it declares one. */
export const discriminatorOf = (def: SchemaNodeOf<'union'>): string | undefined =>
  'discriminator' in def && typeof def.discriminator === 'string' ? def.discriminator : undefined

/**
 * Whether the schema rules this value out: a constant, or a tag field, that disagrees.
 *
 * A nested union is looked through, as it is when asking the opposite question. It pins nothing
 * itself, so on its own terms it never disagrees — but if every branch inside it does, the union as
 * a whole cannot describe the value, and walking it only descends into shapes already excluded.
 */
// A schema cannot describe a value of another shape: an object does not describe a string, a list
// does not describe an object. Absence disagrees with nothing, as above.
//
// Every branch left standing here is one the walk will descend, and a leaf it descends with the
// wrong type throws. So a shape this cannot judge has to stay standing — `unknown`, `any` and the
// opaque `custom` schemas accept anything, and ruling one out would discard the branch that was
// actually the document.
const shapeDisagreesWith = (schema: Schema, value: unknown): boolean => {
  if (value === undefined || value === null) return false
  const def = definitionOf(innerSchema(schema))
  switch (def.type) {
    case 'object':
    case 'record':
      return !isPlainObject(value)
    case 'array':
    case 'tuple':
      return !Array.isArray(value)
    // The one place a value's content decides a branch, and only for a format a string either has or
    // does not: a key naming a standard column cannot also be the uuid of a custom field. Kept to
    // `uuid` deliberately — anything looser starts ruling out branches over stored drift, which is
    // how a real reference disappears. Only reachable from a union, so an annotated field on its own
    // still emits whatever it holds.
    case 'string':
      if (typeof value !== 'string') return true
      return isUuidString(schema) && !isUuid(value)
    case 'number':
      return typeof value !== 'number'
    case 'boolean':
      return typeof value !== 'boolean'
    // On the type its members have, never on membership: a retired value is still the value the
    // document holds, and ruling the branch out for it would lose everything the branch declares.
    case 'enum': {
      const memberTypes = new Set(Object.values(def.entries).map((member) => typeof member))
      return memberTypes.size > 0 && !memberTypes.has(typeof value)
    }
    case 'intersection':
      return shapeDisagreesWith(def.left, value) || shapeDisagreesWith(def.right, value)
    default:
      return false
  }
}

export const isRuledOut = (schema: Schema, value: unknown, discriminator?: string, depth = 0): boolean => {
  if (disagreesWith(schema, value) || shapeDisagreesWith(schema, value)) return true

  const def = definitionOf(innerSchema(schema))
  if (def.type === 'union' && depth < MAX_UNWRAP_DEPTH) {
    const declared = discriminatorOf(def)
    // A declared discriminator the document does not carry rules the union out: a branch admits its
    // absence only by letting the field be left out. Left standing as "cannot tell", it is walked beside
    // the branch that does describe the document, and the walk reports that position unwalkable.
    if (declared !== undefined && isPlainObject(value) && value[declared] === undefined) {
      return !def.options.some((option) => admitsTag(option, declared, undefined))
    }
    return def.options.every((option) => isRuledOut(option, value, declared ?? discriminator, depth + 1))
  }

  if (!isPlainObject(value)) return false
  return tagFieldsOf(schema, discriminator).some(([key, field]) => disagreesWith(field, value[key]))
}

// Zod marks a field that may be missing from the input (optional, defaulted, prefaulted or caught)
// with `optin`, and its discriminated union routes a document without the tag by the same flag.
const admitsAbsence = (field: Schema): boolean => field._zod.optin !== undefined

/**
 * Whether a branch's discriminator admits the value the document holds there.
 *
 * Absence is admitted only by a branch whose field admits it, which is how a schema records that
 * documents predating the discriminator take that shape, as CoreDataValueOperand and extops' primary
 * output config do. Zod routes such a document only when a single branch admits it.
 */
const admitsTag = (option: Schema, discriminator: string, tag: unknown): boolean => {
  const def = definitionOf(innerSchema(option))
  // A branch can itself be a discriminated union: its members share this tag and split on a field of their own.
  if (def.type === 'union') return def.options.some((member) => admitsTag(member, discriminator, tag))
  if (def.type !== 'object') return false

  const field = def.shape[discriminator]
  if (field === undefined) return true
  if (tag === undefined) return admitsAbsence(field)

  // An enum counts here where it does not for an undeclared union. Zod lets a discriminator be one,
  // and a union that names the field has made it authoritative; the reason ordinary enum fields are
  // ignored — retired values dropping a branch — does not apply to a field the schema points at.
  const declared = definitionOf(innerSchema(field))
  if (declared.type === 'enum') return Object.values(declared.entries).some((entry) => entry === tag)
  return fixedValues(field)?.includes(tag) === true
}

type TagVerdict = { discriminator: string; admitted: boolean }

// What a branch that is itself a union with a declared discriminator says about the document's tag.
const tagVerdict = (option: Schema, value: unknown): TagVerdict | undefined => {
  const def = definitionOf(innerSchema(option))
  if (def.type !== 'union' || !isPlainObject(value)) return undefined

  const discriminator = discriminatorOf(def)
  if (discriminator === undefined) return undefined
  return { discriminator, admitted: def.options.some((member) => admitsTag(member, discriminator, value[discriminator])) }
}

/**
 * Which branches of a union to walk.
 *
 * Never decided by parsing: the operand schemas end in `.catch()`, whose handlers report the config
 * as malformed, so probing a branch would make the walk the source of the very signal it must not
 * drown.
 *
 * A branch is excluded only where it disagrees with the value outright. One that pins nothing — a
 * bare uuid, an object of optional fields — takes the document rather than staying silent about it,
 * so what survives is what could still be it. Nothing surviving means nothing can describe this.
 */
/**
 * `identified` is false when the schema does not recognise the document: no branch matched it, and
 * at least one was excluded. The branches left are then the ones that merely fail to disagree, which
 * is not the same as knowing what this is — a caller that must be certain needs to hear that.
 */
export type BranchChoice = { branches: readonly Schema[]; identified: boolean }

export const branchesToWalk = (def: SchemaNodeOf<'union'>, value: unknown): BranchChoice => {
  const options: readonly Schema[] = def.options
  const discriminator = discriminatorOf(def)

  // A declared discriminator is the schema's promise that this one field says which branch a
  // document is, so it answers the question outright — including when the answer is "none of them",
  // which the caller reports rather than guessing past.
  if (discriminator && isPlainObject(value)) {
    const admitted = options.filter((option) => admitsTag(option, discriminator, value[discriminator]))
    return { branches: admitted, identified: admitted.length > 0 }
  }

  // A plain union promised nothing of the sort, so its branches are read three ways rather than two:
  // a branch may match the value, contradict it, or say nothing about it at all. Silence is not
  // agreement, and a contradiction is not a match — so the branches left are those that could still
  // describe the document, whether or not any of them said so.
  const survivors = options.filter((option) => !isRuledOut(option, value))
  const verdicts = survivors.map((option) => tagVerdict(option, value))
  // A tagged branch that names none of its members is out once a sibling tagged on the same field names
  // one. Otherwise it stays, so the position is reported unwalkable rather than every branch dropping.
  const outvoted = (verdict: TagVerdict | undefined): boolean =>
    verdict !== undefined &&
    !verdict.admitted &&
    verdicts.some((sibling) => sibling?.admitted === true && sibling.discriminator === verdict.discriminator)
  const branches = survivors.filter((_, index) => !outvoted(verdicts[index]))
  return { branches, identified: branches.length > 0 }
}
